import { formatDate } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { baseApi, useCheckJobStatusQuery } from "../../../../features/_baseQuery";
import { useBulkCompaniesCSVMutation } from "../../../../features/companyApi";
import { useBulkPeopleCSVMutation } from "../../../../features/peopleApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { ContactType, FindAllContactsFilters } from "../../../lib/filters/dto";
import { initialStateContactsFilters } from "../../../lib/filters/filtersSlice";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { DatabaseSection, downloadCSVfromBase64, TargetSection } from "../../../lib/utils";
import { ConfirmModal } from "../../../ui/organisms/confirmModal.tsx";

export function DownloadBulk({ filters, setFilters, showModal, setShowModal, section, contactType }: { filters: FindAllContactsFilters, setFilters: (filters: FindAllContactsFilters) => void, showModal: boolean, setShowModal: (show: boolean) => void, section: DatabaseSection | TargetSection, contactType: ContactType }) {
    const dispatch = useAppDispatch();
    const [getCSVBulkPeople, {
        isLoading: isGettingCSVBulkPeople,
        reset: resetMutationBulk
    }] = useBulkPeopleCSVMutation();

    const [getCSVBulkCompanies, {
        isLoading: isGettingCSVBulkCompanies,
        reset: resetMutationBulkCompanies
    }] = useBulkCompaniesCSVMutation();

    const [jobIdCompanies, setJobIdCompanies] = useState<string | null>(null);
    const [isPollingCompanies, setIsPollingCompanies] = useState(false);
    const [jobIdPeople, setJobIdPeople] = useState<string | null>(null);
    const [isPollingPeople, setIsPollingPeople] = useState(false);
    const processedJobIds = useRef(new Set<string>());

    const { data: jobStatusCompanies, isError: isPollingErrorCompanies } = useCheckJobStatusQuery(jobIdCompanies!, {
        skip: !jobIdCompanies || !isPollingCompanies,
        pollingInterval: 4000,
    });

    const { data: jobStatusPeople, isError: isPollingErrorPeople } = useCheckJobStatusQuery(jobIdPeople!, {
        skip: !jobIdPeople || !isPollingPeople,
        pollingInterval: 4000,
    });

    const isLoading = isGettingCSVBulkCompanies || isPollingCompanies || isGettingCSVBulkPeople || isPollingPeople;

    useEffect(() => {
        return () => {
            resetMutationBulk();
            resetMutationBulkCompanies();
            setIsPollingCompanies(false);
            setJobIdCompanies(null);
            setIsPollingPeople(false);
            setJobIdPeople(null);
            processedJobIds.current.clear();
        };
    }, [resetMutationBulk, resetMutationBulkCompanies]);

    const handleCloseDownloadModal = () => {
        setShowModal(false);
        resetMutationBulk();
        resetMutationBulkCompanies();
    };

    useEffect(() => {
        if (jobStatusCompanies && (jobStatusCompanies.status === 'COMPLETED' || jobStatusCompanies.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusCompanies.id)) {
                return;
            }

            processedJobIds.current.add(jobStatusCompanies.id);

            setIsPollingCompanies(false);
            setJobIdCompanies(null);

            if (jobStatusCompanies.status === 'COMPLETED') {
                let csvBase64: string = ""

                if (jobStatusCompanies.resultJson) {
                    try {
                        csvBase64 = JSON.parse(jobStatusCompanies.resultJson);
                    } catch (e) {
                        console.error('Errore nel parsing dei risultati:', e);
                        csvBase64 = jobStatusCompanies.resultJson;
                    }
                }

                if (csvBase64) {
                    dispatch(baseApi.util.invalidateTags([{ type: 'Database', id: 'companies' }]));
                    downloadCSVfromBase64(csvBase64, "cd_companies_download_" + formatDate(new Date(), "dd_MM_yyyy"));
                    setFilters({ ...initialStateContactsFilters[section][ContactType.COMPANY] });
                    handleCloseDownloadModal();
                    dispatch(addBannerWithTimeout({
                        message: "File CSV generato con successo",
                        variant: "success"
                    }));
                } else {
                    dispatch(addBannerWithTimeout({
                        message: "Nessun dato CSV trovato nella risposta",
                        variant: "error"
                    }));
                }
            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusCompanies.errorMessage || "Si è verificato un errore durante la generazione del CSV",
                    variant: "error"
                }));
            }
        } else if (isPollingErrorCompanies) {
            setIsPollingCompanies(false);
            setJobIdCompanies(null);
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore imprevisto durante il polling dello stato del job",
                variant: "error"
            }));
        }
    }, [jobStatusCompanies, isPollingErrorCompanies, dispatch, section]);

    useEffect(() => {
        if (jobStatusPeople && (jobStatusPeople.status === 'COMPLETED' || jobStatusPeople.status === 'FAILED')) {
            if (processedJobIds.current.has(jobStatusPeople.id)) {
                return;
            }

            processedJobIds.current.add(jobStatusPeople.id);

            setIsPollingPeople(false);
            setJobIdPeople(null);

            if (jobStatusPeople.status === 'COMPLETED') {
                let csvBase64: string = ""

                if (jobStatusPeople.resultJson) {
                    try {
                        csvBase64 = JSON.parse(jobStatusPeople.resultJson);
                    } catch (e) {
                        console.error('Errore nel parsing dei risultati:', e);
                        csvBase64 = jobStatusPeople.resultJson;
                    }
                }

                if (csvBase64) {
                    dispatch(baseApi.util.invalidateTags([{ type: 'Database', id: 'people' }]));
                    downloadCSVfromBase64(csvBase64, "cd_people_download_" + formatDate(new Date(), "dd_MM_yyyy"));
                    setFilters({ ...initialStateContactsFilters[section][ContactType.PERSON] });
                    handleCloseDownloadModal();
                    dispatch(addBannerWithTimeout({
                        message: "File CSV generato con successo",
                        variant: "success"
                    }));
                } else {
                    dispatch(addBannerWithTimeout({
                        message: "Nessun dato CSV trovato nella risposta",
                        variant: "error"
                    }));
                }
            } else {
                dispatch(addBannerWithTimeout({
                    message: jobStatusPeople.errorMessage || "Si è verificato un errore durante la generazione del CSV",
                    variant: "error"
                }));
            }
        } else if (isPollingErrorPeople) {
            setIsPollingPeople(false);
            setJobIdPeople(null);
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore imprevisto durante il polling dello stato del job",
                variant: "error"
            }));
        }
    }, [jobStatusPeople, isPollingErrorPeople, dispatch, section]);

    const handleDownloadBulkPeopleConfirm = async () => {
        try {
            const response = await getCSVBulkPeople(filters).unwrap();
            setJobIdPeople(response.id);
            setIsPollingPeople(true);
        } catch (error: any) {
            setFilters({ ...initialStateContactsFilters[section][ContactType.PERSON] });
            handleCloseDownloadModal();
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore durante la generazione del file CSV",
                variant: "error"
            }));
        }
    };

    const handleDownloadBulkCompaniesConfirm = async () => {
        try {
            const response = await getCSVBulkCompanies(filters).unwrap();
            setJobIdCompanies(response.id);
            setIsPollingCompanies(true);
        } catch (error: any) {
            setFilters({ ...initialStateContactsFilters[section][ContactType.COMPANY] });
            handleCloseDownloadModal();
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore durante la generazione del file CSV",
                variant: "error"
            }));
        }
    };

    return (
        <ConfirmModal
            key={"download-bulk-people-modal"}
            loading={isLoading}
            title={contactType === ContactType.PERSON ? "Esporta persone" : "Esporta aziende"}
            label={contactType === ContactType.PERSON ? "Sei sicuro di voler esportare tutte le persone trovate?" : "Sei sicuro di voler esportare tutte le aziende trovate?"}
            confirmAction={contactType === ContactType.PERSON ? handleDownloadBulkPeopleConfirm : handleDownloadBulkCompaniesConfirm}
            onClose={handleCloseDownloadModal}
            visible={showModal}
        />
    )
}